import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Leaf, Award, Users, FlaskConical, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

// Optimized gallery images - using smaller sizes for mobile performance
// Note: These images are loaded lazily except the first one
const galleryImages = [
  {
    src: 'https://customer-assets.emergentagent.com/job_static-website-1/artifacts/in0s691l_gallery-2.jpg',
    alt: 'Honey Production Gallery 1'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_static-website-1/artifacts/gzuds9y1_gallery-3.jpg',
    alt: 'Honey Production Gallery 2'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_static-website-1/artifacts/xpv458tw_gallery-5.jpg',
    alt: 'Honey Production Gallery 3'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_static-website-1/artifacts/z93vqt0j_gallery-6.jpg',
    alt: 'Honey Production Gallery 4'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_static-website-1/artifacts/o6gb827n_Med%20i%20cvet.jpg',
    alt: 'Honey and Flowers'
  }
];

// Optimized hero background - heavily compressed for performance
const HERO_BG_IMAGE = 'https://images.unsplash.com/photo-1587049352851-8d4e89133924?w=1200&q=40&fm=webp&fit=crop';
// Mobile version with smaller size and lower quality
const HERO_BG_IMAGE_MOBILE = 'https://images.unsplash.com/photo-1587049352851-8d4e89133924?w=800&q=30&fm=webp&fit=crop';

export default function HomePage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    
    // Auto-advance slider
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const features = [
    {
      icon: <Leaf className="w-10 h-10 text-primary" />,
      title: t('features.quality.title'),
      description: t('features.quality.description')
    },
    {
      icon: <Award className="w-10 h-10 text-primary" />,
      title: t('features.organic.title'),
      description: t('features.organic.description')
    },
    {
      icon: <Users className="w-10 h-10 text-primary" />,
      title: t('features.traditional.title'),
      description: t('features.traditional.description')
    },
    {
      icon: <FlaskConical className="w-10 h-10 text-primary" />,
      title: t('features.tested.title'),
      description: t('features.tested.description')
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay - Using img tag for LCP optimization */}
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_BG_IMAGE_MOBILE}
            srcSet={`${HERO_BG_IMAGE_MOBILE} 800w, ${HERO_BG_IMAGE} 1200w`}
            sizes="100vw"
            alt="Prirodan Med - Organic Honey Background"
            className="w-full h-full object-cover"
            fetchpriority="high"
            decoding="sync"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(32,30%,15%,0.85)] via-[hsl(32,30%,15%,0.75)] to-[hsl(38,92%,50%,0.3)]"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container-custom px-4 sm:px-6 lg:px-8 py-20">
          <div className={`max-w-3xl transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <Badge className="mb-6 bg-primary/20 text-primary-foreground border-primary/30">
              {t('features.quality.title')}
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {t('hero.title')}
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8 leading-relaxed">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => navigate('/products')} 
                size="lg"
                className="btn-honey text-base"
              >
                {t('hero.cta')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Decorative Wave */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path 
              d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,58.7C960,64,1056,64,1152,58.7C1248,53,1344,43,1392,37.3L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" 
              fill="hsl(var(--background))"
            />
          </svg>
        </div>
      </section>

      {/* Gallery Slider Section - deferred loading for performance */}
      <section className="py-16 bg-background">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="relative max-w-6xl mx-auto">
            {/* Slider Container - reduced height on mobile */}
            <div className="relative h-[300px] sm:h-[400px] md:h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              {galleryImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  {/* Only render visible and adjacent slides for performance */}
                  {(index === currentSlide || index === (currentSlide + 1) % galleryImages.length || index === (currentSlide - 1 + galleryImages.length) % galleryImages.length) && (
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover"
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                      width="800"
                      height="500"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/30 to-transparent"></div>
                </div>
              ))}
              
              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-foreground p-3 rounded-full shadow-lg transition-all hover:scale-110 z-10"
                aria-label="Previous slide"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-foreground p-3 rounded-full shadow-lg transition-all hover:scale-110 z-10"
                aria-label="Next slide"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              
              {/* Dots Indicator - Touch targets min 44x44px for accessibility */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                {galleryImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className="min-w-[44px] min-h-[44px] flex items-center justify-center"
                    aria-label={`Go to slide ${index + 1}`}
                  >
                    <span className={`block rounded-full transition-all ${
                      index === currentSlide
                        ? 'bg-white w-6 h-3'
                        : 'bg-white/50 hover:bg-white/75 w-3 h-3'
                    }`} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16 animate-fade-in">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              {t('about.title')}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t('about.description')}
            </p>
          </div>

          {/* Mission Statement */}
          <Card className="max-w-4xl mx-auto bg-gradient-to-br from-secondary to-card border-primary/20 shadow-lg">
            <CardContent className="p-8 sm:p-12 text-center">
              <div className="inline-block p-4 bg-primary/10 rounded-full mb-6">
                <Leaf className="w-12 h-12 text-primary" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-foreground">
                {t('about.subtitle')}
              </h3>
              <p className="text-lg text-muted-foreground italic">
                "{t('about.mission')}"
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-secondary/30">
        <div className="container-custom">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-16 text-foreground">
            {t('features.title')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="card-hover bg-card border-border shadow-md"
                style={{
                  animationDelay: `${index * 100}ms`,
                  animation: 'slideUp 0.6s ease-out forwards',
                  opacity: 0
                }}
              >
                <CardContent className="p-6 text-center flex flex-col items-center">
                  <div className="mb-4 p-4 bg-primary/10 rounded-full">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Products Preview Section */}
      <section className="section-padding bg-background relative overflow-hidden">
        {/* Flying Bee - replaced with CSS-only animation to save 340KB */}
        <div className="hidden md:block absolute w-16 h-16 animate-fly-around z-10 text-4xl" 
             aria-hidden="true"
             style={{ animation: 'flyAround 20s linear infinite' }}>
          🐝
        </div>
        
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-foreground">
              {t('products.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              {t('products.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              {
                image: 'https://images.unsplash.com/photo-1645549826194-1956802d83c2?w=400&q=50&fm=webp&fit=crop',
                title: t('products.meadowHoney'),
                description: t('products.meadowDesc')
              },
              {
                image: 'https://customer-assets.emergentagent.com/job_static-website-1/artifacts/1hxenkfm_Pcela%20jede%20med.jpg',
                title: t('products.acaciaHoney'),
                description: t('products.acaciaDesc')
              },
              {
                image: 'https://images.unsplash.com/photo-1761416351532-ede97c29fab8?w=400&q=50&fm=webp&fit=crop',
                title: t('products.forestHoney'),
                description: t('products.forestDesc')
              }
            ].map((product, index) => (
              <Card key={index} className="card-hover overflow-hidden bg-card border-border shadow-md">
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    loading="lazy"
                    decoding="async"
                    width="400"
                    height="400"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2 text-foreground">{product.title}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{product.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button 
              onClick={() => {
                navigate('/products');
                window.scrollTo(0, 0);
              }} 
              size="lg"
              className="btn-honey"
            >
              {t('products.viewAll')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary to-[hsl(42,100%,65%)] text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
            {t('cta.description')}
          </p>
          <Button 
            onClick={() => navigate('/contact')} 
            size="lg"
            className="bg-white text-primary hover:bg-white/90 shadow-lg font-semibold"
          >
            {t('nav.contact')}
          </Button>
        </div>
      </section>
    </div>
  );
}